import { lazy, Suspense, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import ErrorBoundary from "@jkast90/kast-common/error-boundary";
import { useCacheScopedToUser } from "@jkast90/kast-common/query-client";
import { queryClient } from "@common/queryClient";
import { isSignedIn, SessionProvider, useSession } from "@jkast90/kast-common/auth";
import TooltipHost from "@common/components/TooltipHost";
import { logCrash } from "@common/logCrash";
import { PageBoundary, SiteLayout } from "@common/components/SiteLayout";
import NavRecorder from "@common/components/NavRecorder";
import { adminMode } from "@common/adminMode";
import { session, signIn } from "./auth/session";
import DesktopSignIn from "./desktop/DesktopSignIn";
import DesktopPlaytime from "./desktop/DesktopPlaytime";
import AdoptedSaves from "./library/AdoptedSaves";
import { isDesktop } from "./desktop/desktop";
import { useOfflineSync } from "./emulator/offlineSync";
import AboutPage from "./pages/AboutPage";
import ProfilePage from "./pages/ProfilePage";
import BugReportPage, { BugReport } from "./pages/BugReportPage";
import HomePage from "./pages/HomePage";
import LibraryPage from "./pages/LibraryPage";
import PlayPage from "./pages/PlayPage";

// Its own chunk, with the shared table and its stylesheet: only admins open
// it, and the player (cross-origin isolated, the bulk of visits) never does.
const AdminPage = lazy(() => import("./pages/AdminPage"));

function OfflineSync() {
  useOfflineSync();
  return null;
}

// Not every change of account is a page load (a refresh rejected mid-session
// is not), so the cache is cleared when the account changes, whatever changed it.
function CacheScope() {
  useCacheScopedToUser(queryClient, useSession().user?.sub || null);
  return null;
}

// Report This on the last-resort crash screen: the crash was outside the
// router or the session's provider, so the form shows in place of the app,
// reading the session directly. A page load would lose the logs.
function CrashReport({ crashNote }: { crashNote: string }) {
  const state = useSession(session);
  const user = isSignedIn(state) ? state.user : null;
  return (
    <ErrorBoundary onError={logCrash} buttonClassName="btn" primaryButtonClassName="btn primary">
      <div className="app about">
        <main>
          <BugReport
            user={user ? { id: user.sub, email: user.email || null } : null}
            showLogs={adminMode.isOn(user)}
            onSignIn={signIn}
            initialDescription={crashNote}
          />
        </main>
      </div>
    </ErrorBoundary>
  );
}

export default function App() {
  const [crashNote, setCrashNote] = useState<string | null>(null);
  if (crashNote !== null) return <CrashReport crashNote={crashNote} />;
  return (
    // The last resort, for a crash outside a page (the providers, the top
    // bar, the sign-in dialogs). Pages have their own, below.
    <ErrorBoundary onError={logCrash} onReport={setCrashNote} buttonClassName="btn" primaryButtonClassName="btn primary">
      <QueryClientProvider client={queryClient}>
        <SessionProvider session={session}>
          <CacheScope />
          <BrowserRouter>
            <NavRecorder />
            <DesktopSignIn />
            {isDesktop && <DesktopPlaytime />}
            <OfflineSync />
            <TooltipHost />
            <AdoptedSaves />
            <Routes>
              {/* The player has its own header. Leaving it is a full page
                  load, so Reload is the way out of a crash there, but
                  Report This moves in-app, keeping the logs. */}
              <Route
                path="/play"
                element={
                  <PageBoundary>
                    <PlayPage />
                  </PageBoundary>
                }
              />
              <Route element={<SiteLayout />}>
                <Route path="/" element={<HomePage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/profile" element={<ProfilePage />} />
                <Route
                  path="/admin"
                  element={
                    <Suspense fallback={<p className="muted about-main">Loading…</p>}>
                      <AdminPage />
                    </Suspense>
                  }
                />
                <Route path="/library" element={<LibraryPage />} />
                <Route path="/bug-report" element={<BugReportPage />} />
              </Route>
              {/* Games & players became part of the library. */}
              <Route path="/dashboard" element={<Navigate to="/library" replace />} />
              {/* My games and My saves became the library. */}
              <Route path="/games" element={<Navigate to="/library" replace />} />
              <Route path="/saves" element={<Navigate to="/library" replace />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </SessionProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
